import { useEffect, useRef, useState } from 'react';

import { LocationNodeData } from '../../../../../@types/live-search.type';
import { PartTypeActionLiveSearchItem } from '../../../../../@types/partType.type';
import { useSearchPartsOrPartTypes } from '../../../../../queries/live-search-query';

export interface UsePartOptionsSearchArgs {
  clientContractIds?: number[];
  source: LocationNodeData | null;
}

// Debounced part/part-type live search scoped to the selected source location.
export const usePartOptionsSearch = ({ clientContractIds, source }: UsePartOptionsSearchArgs) => {
  const [partSearchInput, setPartSearchInput] = useState('');
  const [partOptions, setPartOptions] = useState<PartTypeActionLiveSearchItem[]>([]);
  const partSearch = useSearchPartsOrPartTypes();
  // Monotonic id so an out-of-order response can't overwrite the options for
  // what the user is currently typing.
  const searchSequenceRef = useRef(0);

  // Hold the latest values in refs so the debounced search reads current state
  // without re-running the effect on every render (parents may pass a new
  // array reference each time, and useMutation may return a new object
  // reference per render).
  const clientContractIdsRef = useRef(clientContractIds);
  const partSearchRef = useRef(partSearch);
  useEffect(() => {
    clientContractIdsRef.current = clientContractIds;
    partSearchRef.current = partSearch;
  });

  useEffect(() => {
    const sequence = ++searchSequenceRef.current;
    if (partSearchInput.length < 2 || !source?.id) {
      setPartOptions([]);
      return;
    }
    const timer = setTimeout(() => {
      partSearchRef.current
        .mutateAsync({
          name: partSearchInput,
          clientContractIds: clientContractIdsRef.current ?? [],
          locationId: source.id,
          allowedPartLocationType: 'node',
          searchType: 'part-and-part-type',
        })
        .then((data) => {
          if (sequence === searchSequenceRef.current) setPartOptions(data);
        })
        .catch(() => {
          if (sequence === searchSequenceRef.current) setPartOptions([]);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [partSearchInput, source]);

  const clearSearch = () => {
    setPartSearchInput('');
    setPartOptions([]);
  };

  return {
    partSearchInput,
    setPartSearchInput,
    partOptions,
    partSearchPending: partSearch.isPending,
    clearSearch,
  };
};
